/**
 * StudyPlanTabContent
 * Attack plan viewer with syllabus integration
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useStudyPlans, useActiveStudyPlan } from '@/hooks/use-study-plan';
import { useSyllabi } from '@/hooks/use-syllabus';
import { CreateStudyPlanWizard } from '@/components/study-plan/CreateStudyPlanWizard';
import {
  Calendar,
  FileText,
  Plus,
  ChevronRight,
  Clock,
  BookOpen,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface StudyPlanTabContentProps {
  className?: string;
}

export function StudyPlanTabContent({ className }: StudyPlanTabContentProps) {
  const navigate = useNavigate();
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const { isLoading: plansLoading } = useStudyPlans();
  const { data: activePlan, isLoading: activePlanLoading } = useActiveStudyPlan();
  const { data: syllabi = [], isLoading: syllabiLoading } = useSyllabi();

  const isLoading = plansLoading || activePlanLoading || syllabiLoading;

  // Get upcoming exams from syllabi
  const upcomingExams = syllabi
    .flatMap((s) =>
      (s.parsed_data?.exams || []).map((exam) => ({
        ...exam,
        course: s.course_name_extracted || s.file_name,
        syllabusId: s.id,
      }))
    )
    .filter((exam) => {
      if (!exam.date) return false;
      const examDate = new Date(exam.date);
      return examDate > new Date();
    })
    .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime())
    .slice(0, 3);

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        {[1, 2].map((i) => (
          <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Page Title - Gold Standard Pattern */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Study Plan</h1>
        <p className="text-gray-500 dark:text-gray-400">
          {activePlan ? 'Your active attack plan' : 'Plan your path to success'}
        </p>
      </div>

      {/* Active Plan or Create CTA */}
      {activePlan ? (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                Active Attack Plan
              </CardTitle>
              <Badge variant="secondary">{activePlan.status}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">
                  {Math.round((activePlan.progress_percentage || 0) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${(activePlan.progress_percentage || 0) * 100}%` }}
                />
              </div>
              {activePlan.exam_date && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Exam: {new Date(activePlan.exam_date).toLocaleDateString()}
                </p>
              )}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/study-plan/${activePlan.id}`)}
              >
                View Full Plan
                <ChevronRight className="h-4 w-4 ml-auto" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Calendar className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="font-medium mb-1">No Active Attack Plan</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              Create a personalized attack plan based on your syllabi
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Takes about 2 minutes to set up
            </p>
            <Button onClick={() => setShowCreateWizard(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Attack Plan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Attack Plan Sheet */}
      <Sheet open={showCreateWizard} onOpenChange={setShowCreateWizard}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Create Attack Plan</SheetTitle>
          </SheetHeader>
          <CreateStudyPlanWizard
            onComplete={() => {
              setShowCreateWizard(false);
              // Stay in dashboard context - will show the new active plan
            }}
            onCancel={() => setShowCreateWizard(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Upcoming Exams */}
      {upcomingExams.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Upcoming Exams
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {upcomingExams.map((exam, i) => {
              const daysUntil = Math.ceil(
                (new Date(exam.date!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
              );
              return (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div>
                    <p className="text-sm font-medium">{exam.course}</p>
                    <p className="text-xs text-muted-foreground">
                      {exam.name || exam.type}
                    </p>
                  </div>
                  <Badge
                    variant={
                      daysUntil <= 7 ? 'destructive' : daysUntil <= 14 ? 'default' : 'secondary'
                    }
                  >
                    {daysUntil} days
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Syllabi Section */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Your Syllabi
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard/classes')}
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {syllabi.length > 0 ? (
            <div className="space-y-2">
              {syllabi.slice(0, 3).map((syllabus) => (
                <div
                  key={syllabus.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer"
                  onClick={() => navigate(`/dashboard/classes/syllabi/view/${syllabus.id}`)}
                >
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {syllabus.course_name_extracted || syllabus.file_name}
                    </p>
                    {syllabus.professor_name_extracted && (
                      <p className="text-xs text-muted-foreground">
                        Prof. {syllabus.professor_name_extracted}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant={syllabus.status === 'completed' ? 'secondary' : 'outline'}
                    className="text-xs"
                  >
                    {syllabus.status}
                  </Badge>
                </div>
              ))}
              {syllabi.length > 3 && (
                <Button
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => navigate('/dashboard/classes')}
                >
                  View {syllabi.length - 3} more
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-3">
                Upload syllabi to enable smart scheduling
              </p>
              <Button variant="outline" onClick={() => navigate('/dashboard/classes')}>
                <Plus className="h-4 w-4 mr-2" />
                Upload Syllabus
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default StudyPlanTabContent;
